import React, { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { FaDownload } from 'react-icons/fa';
import { AgGridTable, Pagination, Button } from 'components/common';
import {
  setNestedPage,
  setNestedPageSize,
  setNestedParams,
  exportBookings,
} from 'redux/booking/travel/travelBookingSlice';
import { columnDefs } from './Columns';

// Per-status grid. Quick filters live on the page and arrive via `extraParams`.
const TravelBookingGrid = ({ stateKey, loadThunk, exportStatus, extraParams }) => {
  const dispatch = useDispatch();
  const entry = useSelector((s) => s.booking_travel[stateKey]) || {};
  const loading = useSelector((s) => s.booking_travel.loading);
  const exporting = useSelector((s) => s.booking_travel.exporting);

  const { data = [], totalRows = 0, totalPagesCount = 1, page = 1, pageSize = 50, currentParams = {} } = entry;

  const perPageRef = useRef(pageSize);
  useEffect(() => { perPageRef.current = pageSize; }, [pageSize]);

  const extraKey = JSON.stringify(extraParams || {});

  const runFetch = useCallback((pageNum) => {
    const params = { page: pageNum, per_page: perPageRef.current, ...(extraParams || {}) };
    dispatch(setNestedPage({ key: stateKey, page: pageNum }));
    dispatch(setNestedParams({ key: stateKey, params }));
    dispatch(loadThunk(params));
  }, [dispatch, stateKey, loadThunk, extraParams]);

  useEffect(() => { runFetch(1); }, [extraKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (p) => runFetch(p);
  const handlePageSizeChange = (sz) => { perPageRef.current = sz; dispatch(setNestedPageSize({ key: stateKey, pageSize: sz })); runFetch(1); };
  const handleExport = () => dispatch(exportBookings({ ...currentParams, export_status: exportStatus }));

  return (
    <div>
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
        <span className="tw-text-xs tw-text-slate-400 tw-font-medium">{(totalRows || 0).toLocaleString()} records</span>
        <Button variant="secondary" size="sm" leftIcon={FaDownload} loading={exporting} onClick={handleExport}>Export</Button>
      </div>

      <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm tw-overflow-hidden">
        {/* Below `sm` this renders as cards instead of a nine-column grid —
            the four fields someone actually scans when checking a booking from
            a phone. The grid is unchanged from `sm` up. */}
        <AgGridTable
          rowData={data}
          columnDefs={columnDefs}
          pagination={false}
          filter={false}
          height="60vh"
          loading={loading}
          mobileCard={{
            title: 'booking_number',
            subtitle: 'trip_name',
            meta: ['date_of_travel', 'destination', 'workflow_message'],
          }}
        />
        <Pagination
          currentPage={page}
          totalPages={totalPagesCount}
          totalResults={totalRows}
          perPage={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={[50, 100, 200]}
        />
      </div>
    </div>
  );
};

TravelBookingGrid.propTypes = {
  stateKey: PropTypes.string.isRequired,
  loadThunk: PropTypes.func.isRequired,
  exportStatus: PropTypes.string,
  extraParams: PropTypes.object,
};

export default TravelBookingGrid;
